/**
 * menuCatalog.js - Single source of truth for all navigable menu items.
 *
 * Each entry maps a `menuKey` (stored in user_menus table) to its route,
 * display label, icon, allowed roles, and optional access restrictions.
 *
 * Rules:
 *  - `roles`      : which roles can ever see this item
 *  - `globalOnly` : item is hidden unless user.canAccessAllSites === true
 *
 * The Sidebar renders visible items; Users page uses this catalog to show
 * the menu-permission checkboxes. Because both derive from the same list,
 * adding/removing a menu item here automatically keeps both in sync.
 */

import {
  FiLayout,
  FiFileText,
  FiUsers,
  FiServer,
  FiMonitor,
  FiBarChart2,
  FiActivity,
  FiSettings,
  FiClipboard,
  FiUploadCloud,
} from 'react-icons/fi';

/**
 * @typedef {Object} MenuSection
 * @property {string} title
 * @property {MenuEntry[]} items
 */

/**
 * @typedef {Object} MenuEntry
 * @property {string}   menuKey    - key stored in user_menus (must match backend USER_MENU_KEYS)
 * @property {string}   path       - React Router path (leading slash)
 * @property {Function} icon       - react-icons component
 * @property {string}   label      - display label in sidebar / permission UI
 * @property {string[]} roles      - roles that may access this item
 * @property {boolean}  [globalOnly] - true -> hidden for scoped (non-global) users
 */

/** @type {MenuSection[]} */
export const MENU_SECTIONS = [
  {
    title: 'Nghiệp vụ',
    items: [
      {
        menuKey: 'overview.view',
        path: '/dashboard',
        icon: FiLayout,
        label: 'Tổng quan',
        roles: ['SuperAdmin', 'CloudAdmin', 'LocalUser'],
      },
      {
        menuKey: 'exams.view',
        path: '/exams',
        icon: FiFileText,
        label: 'Ca chụp',
        roles: ['SuperAdmin', 'CloudAdmin', 'LocalUser'],
      },
      {
        menuKey: 'patients.view',
        path: '/patients',
        icon: FiClipboard,
        label: 'Bệnh nhân',
        roles: ['SuperAdmin', 'CloudAdmin', 'LocalUser'],
      },
      {
        menuKey: 'stations.manage',
        path: '/stations',
        icon: FiMonitor,
        label: 'Máy chụp',
        roles: ['SuperAdmin', 'CloudAdmin', 'LocalUser'],
      },
      {
        menuKey: 'pacs_sync.upload',
        path: '/pacs-result-import',
        icon: FiUploadCloud,
        label: 'Import PACS',
        roles: ['SuperAdmin', 'LocalAPI'],
      },
      {
        menuKey: 'reports.view',
        path: '/report',
        icon: FiBarChart2,
        label: 'Báo cáo',
        roles: ['SuperAdmin', 'CloudAdmin', 'LocalUser'],
      },
    ],
  },
  {
    title: 'Quản trị',
    items: [
      {
        menuKey: 'sites.manage',
        path: '/sites',
        icon: FiServer,
        label: 'Cơ sở',
        roles: ['SuperAdmin'],
        globalOnly: true,
      },
      {
        menuKey: 'users.manage',
        path: '/users',
        icon: FiUsers,
        label: 'Người dùng',
        roles: ['SuperAdmin', 'CloudAdmin'],
      },
      {
        menuKey: 'settings.manage',
        path: '/sync-jobs',
        icon: FiSettings,
        label: 'Đồng bộ',
        roles: ['SuperAdmin', 'CloudAdmin'],
      },
      {
        menuKey: 'api_tester.view',
        path: '/payload-generator',
        icon: FiSettings,
        label: 'Payload',
        roles: ['SuperAdmin', 'CloudAdmin'],
      },
      {
        menuKey: 'monitoring.view',
        path: '/monitoring',
        icon: FiActivity,
        label: 'Giám sát',
        roles: ['SuperAdmin', 'CloudAdmin'],
      },
      {
        menuKey: 'audit_logs.view',
        path: '/audit-logs',
        icon: FiFileText,
        label: 'Log hệ thống',
        roles: ['SuperAdmin'],
        globalOnly: true,
      },
    ],
  },
];

/** Flat list of all menu entries (used for permission checkboxes) */
export const ALL_MENU_ENTRIES = MENU_SECTIONS.flatMap((s) => s.items);

/** menuKey -> label lookup map */
export const MENU_KEY_LABEL = Object.fromEntries(
  ALL_MENU_ENTRIES.map((e) => [e.menuKey, e.label])
);

/** All valid menu keys (mirrors backend USER_MENU_KEYS) */
export const ALL_MENU_KEYS = ALL_MENU_ENTRIES.map((e) => e.menuKey);
